import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

/**
 * How this project will look to a parent.
 *
 * Round 2 of the form sweep (owner, 2026-10-19: "cleaner, simpler and more
 * non-technical"). Same idea as CategoryPreview: show it rather than describe
 * it, and let the form's own text shrink to one line a field.
 *
 * It draws the two places the fields on this form actually land:
 *
 *   THE CARD, in the projects row on a subject page
 *     at-home-app/src/catalogue/trials/ProjectsRow.tsx:133-146. The name
 *     SPLITS ON ": " -- everything before the first colon is the title, the
 *     rest is the second line. That rule is invisible in a text box and is
 *     the single most surprising thing about this form, which is reason
 *     enough for a preview: "Home Helpers: Machines Around Us" is one field
 *     here and two lines there.
 *     The badge counts the session rows actually filled in below, not
 *     `sessions_count` -- `projectsFromApi` reads `m.sessions?.length` and
 *     falls back to "8 sessions".
 *     `soon` is `status !== 'open'`, so Coming soon AND Inactive both draw
 *     the same stamp. The card stays either way.
 *
 *   THE INTRO on the project page
 *     at-home-app/app/(tabs)/subject/[slug]/module/[id].tsx:496, which reads
 *     `apiModule?.blurb || bundled?.blurb` -- the typed value WINS over the
 *     app's built-in line. Alfred told ops the opposite ("stored, but no app
 *     screen shows it") until this round; all twelve projects on prod have it
 *     filled, so acting on that label would have deleted live copy from
 *     twelve pages.
 *
 * What it does not know, and marks rather than invents: the photograph the
 * app falls back to when none is uploaded, which is bundled per project and
 * does not live in Alfred.
 */

const useStyles = makeStyles((theme) => ({
  root: { position: 'sticky', top: 0 },
  heading: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 6
  },
  sub: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 12,
    lineHeight: 1.4
  },
  tile: {
    width: 230,
    maxWidth: '100%',
    background: '#fff',
    border: '1px solid #EADFD6',
    borderRadius: 16,
    overflow: 'hidden'
  },
  photoWrap: { position: 'relative' },
  photo: {
    width: '100%',
    height: 120,
    objectFit: 'cover',
    display: 'block',
    background: '#F0E7E0'
  },
  photoNone: {
    width: '100%',
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 16px',
    boxSizing: 'border-box',
    background: '#EFF4EA',
    color: '#8A9A7B',
    fontSize: 11,
    lineHeight: 1.35
  },
  badge: {
    position: 'absolute',
    top: 8,
    right: 8,
    background: '#fff',
    borderRadius: 999,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: '#3D2B1F'
  },
  soonStamp: {
    position: 'absolute',
    top: 8,
    left: 8,
    background: '#E6DAD0',
    color: '#7A6757',
    borderRadius: 999,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 700
  },
  tx: { padding: '10px 12px 12px' },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: '#3D2B1F',
    lineHeight: '18px'
  },
  subtitle: {
    fontSize: 12,
    color: '#7A6757',
    lineHeight: '16px',
    marginTop: 2
  },
  note: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 10,
    lineHeight: 1.45
  },
  block: { marginTop: 18 },
  page: {
    background: '#fff',
    border: '1px solid #EADFD6',
    borderRadius: 12,
    padding: '10px 12px'
  },
  pageTitle: { fontSize: 14, fontWeight: 700, color: '#3D2B1F' },
  intro: { fontSize: 12, color: '#5C4636', lineHeight: 1.5, marginTop: 4 },
  introNone: {
    fontSize: 12,
    color: '#B9A89B',
    fontStyle: 'italic',
    marginTop: 4
  }
}))

const ProjectPreview = ({ values }) => {
  const classes = useStyles()
  const raw = (values.name || '').trim()
  // ": " exactly, as ProjectsRow splits it -- a colon with no space does not
  // make a second line, which is itself worth seeing before you save.
  const [title, ...rest] = raw.split(': ')
  const subtitle = rest.length ? rest.join(': ') : ''
  const photo = (values.image_url || '').trim()
  const blurb = (values.blurb || '').trim()
  const soon = (values.status || 'open') !== 'open'
  // The same rows formToPayload sends: numbered, and with a title.
  const filled = (values.sessions || []).filter(
    (r) => r.n >= 1 && (r.name || '').trim()
  ).length
  const badge = `${filled || 8} sessions`

  return (
    <div className={classes.root}>
      <div className={classes.heading}>On the subject&apos;s page</div>
      <div className={classes.sub}>
        The card a parent taps. Anything after a colon becomes the second line.
      </div>

      <div className={classes.tile}>
        <div className={classes.photoWrap}>
          {photo ? (
            <img src={photo} alt="" className={classes.photo} />
          ) : (
            <div className={classes.photoNone}>
              the app&apos;s own drawing for this project
            </div>
          )}
          <span className={classes.badge}>{badge}</span>
          {soon ? <span className={classes.soonStamp}>Coming soon</span> : null}
        </div>
        <div className={classes.tx}>
          <div className={classes.title}>{title || 'Project name'}</div>
          {subtitle ? <div className={classes.subtitle}>{subtitle}</div> : null}
        </div>
      </div>

      <div className={classes.note}>
        {soon
          ? 'Not bookable. The card stays on the subject page either way — neither Coming soon nor Inactive takes it out of the app.'
          : `The badge counts the sessions you have filled in below${
              filled ? '' : ' — none yet, so it reads 8'
            }.`}
      </div>

      <div className={classes.block}>
        <div className={classes.heading}>At the top of the project page</div>
        <div className={classes.page}>
          <div className={classes.pageTitle}>{raw || 'Project name'}</div>
          {blurb ? (
            <div className={classes.intro}>{blurb}</div>
          ) : (
            <div className={classes.introNone}>
              blank — the app&apos;s own line shows here
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

ProjectPreview.propTypes = { values: PropTypes.object.isRequired }

export default ProjectPreview
